import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import { Flex } from 'styling/GlobalStyles'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { Input } from 'components/Input/Input'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { tokens } from 'styling/tokens'
import { GradeBasis } from 'types/materialReceipt.interface'
import { setBasis, setThreshold } from 'features/materialReceipt/materialReceiptSlice'
import { discrepancyOf } from './materialReceiptData'
import { MaterialReceiptPage } from './MaterialReceiptShell'
import { ConfigOption, Grid2 } from './MaterialReceipt.styled'

const BASIS_COPY: { [k in GradeBasis]: { title: string; formula: string; note: string } } = {
  DRY: { title: 'Dry basis', formula: 'gold = net dry weight × grade', note: 'Moisture is removed before grade is applied. Conservative; standard for settlement.' },
  WET: { title: 'Wet basis', formula: 'gold = net wet weight × grade', note: 'Grade applied to as-received tonnage. Higher figure; used by some off-take terms.' },
}

export const MaterialConfig = () => {
  const dispatch = useDispatch<AppDispatch>()
  const { deliveries, config } = useSelector((s: RootState) => s.materialReceipt)

  const breaches = deliveries.filter((d) => discrepancyOf(d, config.threshold)?.breach).length

  return (
    <MaterialReceiptPage requireManager title='Site config' subtitle='Applies to all yield and discrepancy calculations'>
      {() => (
        <>
          <Grid2>
            <Container>
              <ContainerHeader><TitleText weight='bold'>Grade basis</TitleText></ContainerHeader>
              <ContainerContent>
                <Flex gap='0.75rem' mb='16'>
                  {(['DRY', 'WET'] as GradeBasis[]).map((b) => (
                    <ConfigOption key={b} $active={config.basis === b} onClick={() => dispatch(setBasis(b))}>
                      <Flex between center mb='8'>
                        <BodyText weight='bold' style={{ margin: 0, color: config.basis === b ? tokens.accent.base : tokens.text.primary }}>
                          {BASIS_COPY[b].title}
                        </BodyText>
                        {config.basis === b && <StatusPill status='accent'>active</StatusPill>}
                      </Flex>
                      <OtherText grey style={{ fontFamily: 'monospace' }}>{BASIS_COPY[b].formula}</OtherText>
                    </ConfigOption>
                  ))}
                </Flex>
                <OtherText grey>{BASIS_COPY[config.basis].note}</OtherText>
              </ContainerContent>
            </Container>

            <Container>
              <ContainerHeader><TitleText weight='bold'>Discrepancy threshold</TitleText></ContainerHeader>
              <ContainerContent>
                <BodyText grey style={{ marginBottom: '0.875rem' }}>
                  When the arrival grade differs from the pre-shipment grade by more than this percentage, a discrepancy case is opened automatically.
                </BodyText>
                <Input
                  type='number'
                  labelText='Tolerance (± %)'
                  value={String(config.threshold)}
                  onChange={(e) => dispatch(setThreshold(Math.max(0, Number(e.target.value) || 0)))}
                  nomarg
                />
                <Flex center gap='0.5rem' mt='16'>
                  <BodyText style={{ margin: 0 }}>At ±{config.threshold}%:</BodyText>
                  <StatusPill status={breaches ? 'danger' : 'success'}>
                    {breaches} {breaches === 1 ? 'case' : 'cases'} flagged
                  </StatusPill>
                </Flex>
              </ContainerContent>
            </Container>
          </Grid2>
        </>
      )}
    </MaterialReceiptPage>
  )
}
